import React, { useEffect, useState } from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { Box, HStack, Text, VStack, useMediaQuery, Pressable, useToast, Tooltip, IconButton } from 'native-base';
import Svg from 'react-native-svg';
import { EventAnalysisDashboard } from '../components/EventAnalysisDashboard.tsx';
import MultiVendorBenchmarkingDashboard from '../components/MultiVendorBenchmarkingDashboard.tsx';
import QuotationAnalysisDashboardV2 from '../components/QuotationAnalysisDashboardV2.tsx';
import { DashboardHeader } from '../components/DashboardHeader.tsx';
import WebHeader from '../layout/header/webHeader';
import MobileHeader from '../layout/header/mobileHeader';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../utils/fonts';
import { COLORS } from '../utils/colors';
import { MOBILE_VIEW_WIDTH } from '../utils/screens';
import CustomButton from '../components/button';
import { ArrowLeft, RefreshCw, Download } from 'lucide-react';
import { quotationCostBenchmarkingRerunAPI, downloadQuotationAnalysisPDF } from '../api/apis/quicklinks';
import { Toaster } from '../components/toaster';
import DataTransformer from '../utilsFunction/dataTransformer.ts';
import CustomModal from '../components/modal';
import MobileModelSheet from '../components/actionSheet';
import { breadCrumbsArrowSVG } from '../utils/svg';
import NoDataFound from '../components/noDataFound';
import { LoaderAnimation } from '../components/spinner';
import '../containers/documents/style.css';

// Extract multi-vendor result from API response
function extractResult(data: any) {
  const responseData = data?.response || data;
  
  if (!responseData) {
    console.error('No response data found');
    return [];
  }
  
  const output = responseData.output || responseData;
  const content = output?.content?.main || output?.content;
  
  if (!content || !Array.isArray(content) || content.length === 0) {
    console.error('No content array found in response');
    return [];
  }
  
  const contentString = content[0]?.content;
  
  if (typeof contentString !== 'string') {
    console.error('Content is not a string:', typeof contentString, contentString);
    return [];
  }
  
  let startIdx = contentString.indexOf('{');
  let endIdx = contentString.lastIndexOf('}');
  
  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) {
    console.error('No valid JSON object found in content string');
    return [];
  }
  
  let jsonString = contentString.slice(startIdx, endIdx + 1);
  
  try {
    const parsed = JSON.parse(jsonString);
    
    if (!Array.isArray(parsed.result)) {
      console.error('Parsed content does not contain a result array:', parsed);
      return [];
    }
    
    const flattened: any[] = [];
    parsed.result.forEach((item: any) => {
      const HEAD = item.HEAD || '';
      const SUB_HEAD = item['SUB HEAD'] || '';
      const SIZE = item.SIZE || '';
      const QTY = item.QTY || '';
      const matches = item.matches || [];
      const match_count = item.match_count || 0;
      
      if (Array.isArray(item.DETAILS)) {
        item.DETAILS.forEach((detail: any) => {
          flattened.push({
            HEAD: HEAD,
            'SUB HEAD': SUB_HEAD,
            DETAILS: detail.description || '',
            SIZE: SIZE,
            QTY: QTY,
            'FINAL COST (₹)': detail.final_cost || '',
            vendor_name: detail.vendor_name || '',
            VENDOR_NAME: detail.vendor_name || '',
            unit_price: detail.unit_price || 0,
            matches: matches,
            match_count: match_count,
          });
        });
      } else {
        flattened.push({
          HEAD: HEAD,
          'SUB HEAD': SUB_HEAD,
          DETAILS: item.DETAILS || '',
          SIZE: SIZE,
          QTY: QTY,
          'FINAL COST (₹)': '',
          vendor_name: '',
          VENDOR_NAME: '',
          unit_price: 0,
          matches: matches,
          match_count: match_count,
        });
      }
    });
    
    return flattened;
  } catch (err) {
    console.error('Failed to parse JSON dashboard content:', err);
    console.error('JSON string:', jsonString.substring(0, 500));
    return [];
  }
}

// Benchmark JSON Viewer Component
const BenchmarkJsonViewer = ({ data }: { data: any }) => {
  const [isJsonVisible, setIsJsonVisible] = useState(true);

  return (
    <Box bg="white" borderRadius="xl" shadow="sm" p={6} overflow="auto" borderWidth={1} borderColor="gray.200">
      <Text
        fontWeight={COMMONFONTSIZE.LARGE_FONT_WEIGHT}
        fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
        color={COLORS.ACCORDION_TITLE_COLOR}
        mb={4}
      >
        Benchmark Data
      </Text>
      
      {data ? (
        <VStack space={4}>
          {/* View Full Data Link */}
          <Pressable
            onPress={() => setIsJsonVisible(!isJsonVisible)}
            bg="transparent"
            borderWidth={1}
            borderColor={COLORS.THEME_COLOR}
            borderRadius="md"
            px={4}
            py={2}
            alignItems="center"
          >
            <Text fontSize="sm" color={COLORS.THEME_COLOR} fontWeight="medium">
              {isJsonVisible ? 'Hide JSON Data' : 'View JSON Data'}
            </Text>
          </Pressable>
          
          {isJsonVisible && (
            <Box bg="gray.50" p={4} borderRadius="md" borderWidth={1} borderColor="gray.200">
              <Text fontSize="xs" fontFamily="mono" color="gray.700">
                {JSON.stringify(data, null, 2)}
              </Text>
            </Box>
          )}
        </VStack>
      ) : (
        <Text color="primary.500">No benchmark data found</Text>
      )}
    </Box>
  );
};

interface InsightsData {
  sessionData?: any;
  transformedData?: any;
  benchmarkData?: any;
  multiVendorData?: any;
  quotationAnalysis?: any;
  eventDetails: any;
  mode: 'quotation' | 'benchmark' | 'multi-vendor' | 'quotation-analysis';
  dataSource?: 'abg-event-bench' | 're-run' | 'file-upload';
}

const EventInsightsPage = () => {
  const location = useLocation();
  const history = useHistory();
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH,
  });

  const [insightsData, setInsightsData] = useState<InsightsData | null>(null);
  const [activeTab, setActiveTab] = useState('Vendor to Vendor');
  const [isRunningRerun, setIsRunningRerun] = useState(false);
  const [shimmerTextIndex, setShimmerTextIndex] = useState(0);
  const [showRerunLoadingModal, setShowRerunLoadingModal] = useState(false);
  const [rerunError, setRerunError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const toaster = Toaster();
  const toast = useToast();

  // Array of shimmering texts to cycle through
  const shimmerTexts = [
    "Please wait while Param AI Studio is doing its magic✨",
    "Analyzing vendor data for cost benchmarking",
    "Processing market intelligence from multiple sources",
    "Refining data from vendors",
    "Analyzing reference data against these vendors",
    "Calculating cost variance metrics across categories",
    "Comparing your spend against market benchmarks",
    "Generating insights from procurement patterns",
    "Building comparative analysis charts",
    "Finalizing dashboard visualizations",
    "Almost ready - preparing your cost benchmark report"
  ];

  // Cycle through shimmer texts when loading
  useEffect(() => {
    let interval: any;
    if (isRunningRerun || showRerunLoadingModal) {
      interval = setInterval(() => {
        setShimmerTextIndex((prevIndex) => 
          (prevIndex + 1) % shimmerTexts.length
        );
      }, 6000); // Change text every 6 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunningRerun, showRerunLoadingModal, shimmerTexts.length]);

  useEffect(() => {
    // Get insights data from location state
    if (location.state && location.state.insightsData) {
      const incomingData = location.state.insightsData;
      
      // Handle quotation-analysis mode
      if (incomingData.mode === 'quotation-analysis' && incomingData.quotationAnalysis) {
        setInsightsData(incomingData);
        return;
      }
      
      // Detect if sessionData contains multi-vendor format
      if (incomingData.sessionData) {
        const responseData = incomingData.sessionData?.response || incomingData.sessionData;
        const output = responseData?.output || responseData;
        const hasMainContent = output?.content?.main;
        
        if (hasMainContent && !incomingData.multiVendorData) {
          // This is multi-vendor format but not yet processed
          const resultArray = extractResult(incomingData.sessionData);
          
          if (resultArray && resultArray.length > 0) {
            const updatedData = {
              ...incomingData,
              multiVendorData: { result: resultArray },
              mode: 'multi-vendor' as const,
              dataSource: incomingData.transformedData?.dataSource || incomingData.dataSource || 'abg-event-bench'
            };
            setInsightsData(updatedData);
          } else {
            setInsightsData({
              ...incomingData,
              dataSource: incomingData.transformedData?.dataSource || incomingData.dataSource || 'abg-event-bench'
            });
          }
        } else {
          setInsightsData({
            ...incomingData,
            dataSource: incomingData.transformedData?.dataSource || incomingData.dataSource || 'abg-event-bench'
          });
        }
      } else {
        setInsightsData(incomingData);
      }
      
      // Only set active tab if we have transformedData (quotation mode)
      if (incomingData.transformedData && incomingData.transformedData.tabs) {
        setActiveTab(incomingData.transformedData.tabs[0] || 'Vendor to Vendor');
      }
    } else {
      // If no data, redirect back to event details
      history.push('/event-detail');
    }
  }, [location.state, history]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleBackToEvent = () => {
    history.push('/event-detail');
  };

  const closeRerunLoadingModal = () => {
    // Only allow closing if not actively running
    if (!isRunningRerun) {
      setShowRerunLoadingModal(false);
      setIsRunningRerun(false);
      setRerunError(null);
    }
  };

  const handleReRun = async () => {
    if (!insightsData?.eventDetails?._id) {
      toaster("Event ID not found", "error");
      return;
    }

    // Close all existing toasts to prevent double toast issue
    toast.closeAll();
    
    setIsRunningRerun(true);
    setShowRerunLoadingModal(true);
    setShimmerTextIndex(0);
    setRerunError(null);

    try {
      // Call the Re-Run API with run=run_mind parameter
      const sessionData = await quotationCostBenchmarkingRerunAPI(insightsData?.eventDetails._id);
      
      // Detect if this is multi-vendor format
      const responseData = sessionData?.response || sessionData;
      const output = responseData?.output || responseData;
      const hasMainContent = output?.content?.main;
      
      let updatedInsightsData;
      
      if (hasMainContent) {
        // Multi-vendor format
        const resultArray = extractResult(sessionData);
        
        if (resultArray && resultArray.length > 0) {
          updatedInsightsData = {
            ...insightsData,
            sessionData: sessionData,
            multiVendorData: { result: resultArray },
            transformedData: null,
            mode: 'multi-vendor',
            dataSource: 're-run'
          };
        } else {
          throw new Error('Failed to extract multi-vendor data from re-run');
        }
      } else {
        // Standard format - Transform data for the dashboard
        const transformedDataRaw = DataTransformer.transformSessionData(sessionData);
        const allowedTabs = ['Vendor to Vendor', 'Vendor to Benchmark'];
        const filteredTabs = transformedDataRaw.tabs.filter((tab) => allowedTabs.includes(tab));
        const transformedData = {
          ...transformedDataRaw,
          tabs: filteredTabs,
          dataSource: 're-run',
          sessionId: sessionData._id
        };
        
        updatedInsightsData = {
          ...insightsData,
          sessionData: sessionData,
          transformedData: transformedData,
          multiVendorData: null,
          mode: 'quotation',
          dataSource: 're-run'
        };
      }
      
      setInsightsData(updatedInsightsData);
      
      // Update location.state to persist the new data on refresh
      history.replace('/cost-benchmarking', {
        insightsData: updatedInsightsData
      });
      
      setIsRunningRerun(false);
      setShowRerunLoadingModal(false);
      
      // Close all existing toasts before showing success message
      toast.closeAll();
      toaster("Cost benchmarking data updated successfully!", "success");
      
    } catch (error) {
      setRerunError(error?.message || 'Failed to update cost benchmarking data');
      setIsRunningRerun(false);
      setShowRerunLoadingModal(false);
      
      // Close all existing toasts before showing error message
      toast.closeAll();
      toaster(error.message || 'Failed to update cost benchmarking data', "error");
    }
  };

  const handleDownloadQuotationPDF = async () => {
    if (!eventDetails?._id || isDownloading) return;

    try {
      setIsDownloading(true);
      LoaderAnimation.show();
      toast.closeAll();

      const response = await downloadQuotationAnalysisPDF(eventDetails._id);
      const url = window.URL.createObjectURL(response?.data);
      const link = document.createElement('a');
      const fallbackName = `${eventDetails?.event_name}_quotation_analysis.pdf`;
      link.href = url;
      link.setAttribute('download', response?.filename || fallbackName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toaster('Quotation Analysis Report downloaded successfully!', 'success');
    } catch (error) {
      toaster(
        error?.message || 'Failed to download quotation analysis report. Please try again later.',
        'error'
      );
    } finally {
      LoaderAnimation.hide();
      setIsDownloading(false);
    }
  };

  // Don't show loading state - if no data, redirect immediately (handled in useEffect)
  if (!insightsData) {
    return null; // useEffect will handle redirect
  }

  const { sessionData, transformedData, benchmarkData, multiVendorData, quotationAnalysis, eventDetails, mode, dataSource } = insightsData;

  // Extract the actual analysis data from nested structure
  // API response structure: { data: { data: {...}, message: "...", status: "success" }, error: false }
  // So we need to access quotationAnalysis.data.data for the actual analysis data
  const analysisData = quotationAnalysis?.data?.data || quotationAnalysis?.data || quotationAnalysis;

  // Additional safety check
  if (!mode || !eventDetails) {
    console.warn('⚠️ Invalid data format - Missing mode or eventDetails');
    return (
      <Box minH="100vh" bg="gray.50" p={4}>
        <Text>Invalid data format. Redirecting...</Text>
      </Box>
    );
  }

  // Determine event tab for breadcrumb
  const eventTab = eventDetails?.type === 'mice' ? 'MICE' : 'Event';

  // Breadcrumb configuration for quotation-analysis mode
  const breadcrumbDetails = {
    activeTab: { name: 'Home', path: '/home' },
    activeMenu: { name: `${eventTab} Details`, path: '/event-detail' },
    activeActivePage: { name: mode === 'quotation-analysis' ? 'ABG Event Bench' : '', path: '/cost-benchmarking' }
  };

  return (
    <Box minH="100vh" bg={COLORS.THEME_BACKGROUND}>
      {/* WebHeader and MobileHeader */}
      <Box bg={COLORS.WHITE_COLOR}>
        {MobileWindow ? (
          <MobileHeader showTabs={false} />
        ) : (
          <>
            <WebHeader showTabs={false} />
            {/* Breadcrumb - Only show for quotation-analysis mode on desktop */}
            {mode === 'quotation-analysis' && (
              <Box p={4} px={'5rem'}>
                <HStack space={2} alignItems={"center"}>
                  <Link
                    style={{
                      textDecoration: "none",
                      color: "rgba(0, 0, 0, 0.50)",
                      fontSize: COMMONFONTSIZE.MEDIUM_FONT_SIZE,
                    }}
                    to={breadcrumbDetails.activeTab.path}
                  >
                    {breadcrumbDetails.activeTab.name}
                  </Link>
                  {breadcrumbDetails.activeMenu.name !== "" && (
                    <>
                      <Svg width="16" height="17" viewBox="0 0 16 17">
                        {breadCrumbsArrowSVG}
                      </Svg>
                      <Link
                        style={{
                          textDecoration: "none",
                          color: COLORS.BLACK_COLOR,
                          fontSize: COMMONFONTSIZE.MEDIUM_FONT_SIZE,
                        }}
                        to={breadcrumbDetails.activeMenu.path}
                      >
                        {breadcrumbDetails.activeMenu.name}
                      </Link>
                    </>
                  )}
                  {breadcrumbDetails.activeActivePage.name !== "" && (
                    <>
                      <Svg width="16" height="17" viewBox="0 0 16 17">
                        {breadCrumbsArrowSVG}
                      </Svg>
                      <Text
                        color={COLORS.BLACK_COLOR}
                        fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                      >
                        {breadcrumbDetails.activeActivePage.name}
                      </Text>
                    </>
                  )}
                </HStack>
              </Box>
            )}
          </>
        )}
      </Box>

      <Box width={MobileWindow ? "100%" : "98%"} mx="auto" px={MobileWindow ? '13px' : '4rem'} py={6}>
        {/* Breadcrumb - Only show for quotation-analysis mode on mobile */}
        {mode === 'quotation-analysis' && MobileWindow && (
          <Box mb={4}>
            <HStack space={2} alignItems={"center"}>
              <Link
                style={{
                  textDecoration: "none",
                  color: "rgba(0, 0, 0, 0.50)",
                  fontSize: COMMONFONTSIZE.MEDIUM_FONT_SIZE,
                }}
                to={breadcrumbDetails.activeTab.path}
              >
                {breadcrumbDetails.activeTab.name}
              </Link>
              {breadcrumbDetails.activeMenu.name !== "" && (
                <>
                  <Svg width="16" height="17" viewBox="0 0 16 17">
                    {breadCrumbsArrowSVG}
                  </Svg>
                  <Link
                    style={{
                      textDecoration: "none",
                      color: COLORS.BLACK_COLOR,
                      fontSize: COMMONFONTSIZE.MEDIUM_FONT_SIZE,
                    }}
                    to={breadcrumbDetails.activeMenu.path}
                  >
                    {breadcrumbDetails.activeMenu.name}
                  </Link>
                </>
              )}
              {breadcrumbDetails.activeActivePage.name !== "" && (
                <>
                  <Svg width="16" height="17" viewBox="0 0 16 17">
                    {breadCrumbsArrowSVG}
                  </Svg>
                  <Text
                    color={COLORS.BLACK_COLOR}
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                  >
                    {breadcrumbDetails.activeActivePage.name}
                  </Text>
                </>
              )}
            </HStack>
          </Box>
        )}

        {/* Header - Only show for non-quotation-analysis modes */}
        {mode !== 'quotation-analysis' && (
          <HStack justifyContent="space-between" alignItems="center" mb={6}>
            <HStack space={4} alignItems="center">
              <Pressable
                onPress={handleBackToEvent}
                bg={COLORS.THEME_COLOR}
                borderRadius="full"
                p={3}
                alignItems="center"
                justifyContent="center"
                shadow="sm"
              >
                <ArrowLeft size={20} color="white" />
              </Pressable>
              <VStack>
                <Text
                  fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                  fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
                  color={COLORS.ACCORDION_TITLE_COLOR}
                >
                  {(multiVendorData || transformedData) ? 'Event Insights' : 'ABG Event Bench'}
                </Text>
                {(dataSource || transformedData?.dataSource) && (
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    mt={1}
                  >
                    Data Source: {
                      (dataSource || transformedData?.dataSource) === 'abg-event-bench' ? 'ABG Event Bench' : 
                      (dataSource || transformedData?.dataSource) === 're-run' ? 'Re-Run' : 
                      'File Upload'
                    }
                  </Text>
                )}
              </VStack>
            </HStack>
            
            {/* Re-Run Button - Only show for quotation and multi-vendor modes */}
            {/* {(multiVendorData || mode === 'quotation') && (
              <CustomButton
                onPress={handleReRun}
                text={isRunningRerun ? "Re-Running..." : "Re-Run"}
                bgColor={COLORS.THEME_COLOR}
                isDisabled={isRunningRerun}
                width="auto"
                pl={4}
                pr={4}
                // @ts-ignore - startIcon accepts JSX elements via native-base Button
                startIcon={<RefreshCw size={16} color="white" />}
              />
            )} */}
          </HStack>
        )}
        {/* Event Details Heading - Outside the card */}
        <HStack alignItems="center" justifyContent="space-between" mb={4}>
          <Text
            fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
            fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
            color={COLORS.BLACK_COLOR}
          >
            ABG Event Bench
          </Text>
          {mode === 'quotation-analysis' && (
            <Tooltip label="Download as PDF" placement="left">
              <IconButton
                icon={<Download size={20} color={isDownloading ? '#ccc' : COLORS.THEME_COLOR} />}
                onPress={handleDownloadQuotationPDF}
                isDisabled={isDownloading}
                variant="ghost"
                _hover={{ bg: 'gray.100' }}
                borderRadius="lg"
                p={2}
                opacity={isDownloading ? 0.5 : 1}
                aria-label="Download as PDF"
              />
            </Tooltip>
          )}
        </HStack>

        {/* Event Details Card */}
        {/* <Box bg="white" borderRadius="xl" shadow="sm" p={6} mb={6} borderWidth={1} borderColor="gray.200">
          <Text
            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
            color={COLORS.TEXT_COLOR}
            mb={4}
          >
            Event Name : {eventDetails?.event_name || 'N/A'}
          </Text>
          <Box borderWidth={1} borderColor={COLORS.BORDER_COLOR} borderRadius="sm" overflow="hidden">
            <HStack borderBottomWidth={1} borderBottomColor={COLORS.BORDER_COLOR}>
              <Box flex={1} borderRightWidth={1} borderRightColor={COLORS.BORDER_COLOR}>
                <HStack>
                  <Box
                    p={3}
                    borderRightWidth={1}
                    borderRightColor={COLORS.BORDER_COLOR}
                    bg="#f5f5f5"
                    width="40%"
                    minWidth="100px"
                  >
                    <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                      Event Type
                    </Text>
                  </Box>
                  <Box p={3} flex={1}>
                    <Text
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                      color={COLORS.TEXT_COLOR || "#1a1a1a"}
                    >
                      {eventDetails?.type || 'N/A'}
                    </Text>
                  </Box>
                </HStack>
              </Box>
              <Box flex={1}>
                <HStack>
                  <Box
                    p={3}
                    borderRightWidth={1}
                    borderRightColor={COLORS.BORDER_COLOR}
                    bg="#f5f5f5"
                    width="40%"
                    minWidth="100px"
                  >
                    <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                      Status
                    </Text>
                  </Box>
                  <Box p={3} flex={1}>
                    <Text
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                      color={COLORS.TEXT_COLOR || "#1a1a1a"}
                      textTransform="capitalize"
                    >
                      {eventDetails?.status?.replace('_', ' ') || 'N/A'}
                    </Text>
                  </Box>
                </HStack>
              </Box>
            </HStack>
            <HStack borderBottomWidth={1} borderBottomColor={COLORS.BORDER_COLOR}>
              <Box flex={1} borderRightWidth={1} borderRightColor={COLORS.BORDER_COLOR}>
                <HStack>
                  <Box
                    p={3}
                    borderRightWidth={1}
                    borderRightColor={COLORS.BORDER_COLOR}
                    bg="#f5f5f5"
                    width="40%"
                    minWidth="100px"
                  >
                    <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                      Start Date
                    </Text>
                  </Box>
                  <Box p={3} flex={1}>
                    <Text
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                      color={COLORS.TEXT_COLOR || "#1a1a1a"}
                    >
                      {eventDetails?.from_date ? new Date(eventDetails.from_date).toLocaleDateString() : 'N/A'}
                    </Text>
                  </Box>
                </HStack>
              </Box>
              <Box flex={1}>
                <HStack>
                  <Box
                    p={3}
                    borderRightWidth={1}
                    borderRightColor={COLORS.BORDER_COLOR}
                    bg="#f5f5f5"
                    width="40%"
                    minWidth="100px"
                  >
                    <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                      End Date
                    </Text>
                  </Box>
                  <Box p={3} flex={1}>
                    <Text
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                      color={COLORS.TEXT_COLOR || "#1a1a1a"}
                    >
                      {eventDetails?.to_date ? new Date(eventDetails.to_date).toLocaleDateString() : 'N/A'}
                    </Text>
                  </Box>
                </HStack>
              </Box>
            </HStack>
            <HStack>
              <Box flex={1} borderRightWidth={1} borderRightColor={COLORS.BORDER_COLOR}>
                <HStack>
                  <Box
                    p={3}
                    borderRightWidth={1}
                    borderRightColor={COLORS.BORDER_COLOR}
                    bg="#f5f5f5"
                    width="40%"
                    minWidth="100px"
                  >
                    <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                      Coordinator
                    </Text>
                  </Box>
                  <Box p={3} flex={1}>
                    <Text
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                      color={COLORS.TEXT_COLOR || "#1a1a1a"}
                    >
                      {eventDetails?.coordinator_name || 'N/A'}
                    </Text>
                  </Box>
                </HStack>
              </Box>
              <Box flex={1}>
                <HStack>
                  <Box
                    p={3}
                    borderRightWidth={1}
                    borderRightColor={COLORS.BORDER_COLOR}
                    bg="#f5f5f5"
                    width="40%"
                    minWidth="100px"
                  >
                    <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                      Contact Email
                    </Text>
                  </Box>
                  <Box p={3} flex={1}>
                    <Text
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                      color={COLORS.TEXT_COLOR || "#1a1a1a"}
                    >
                      {eventDetails?.contact_details?.email || 'N/A'}
                    </Text>
                  </Box>
                </HStack>
              </Box>
            </HStack>
          </Box>
        </Box> */}

        {/* Dashboard Header - Only for quotation mode */}
        {mode === 'quotation' && sessionData && (
          <Box mb={6}>
            <DashboardHeader session={sessionData} />
          </Box>
        )}

        {/* Tab Navigation - Only for quotation mode */}
        {mode === 'quotation' && transformedData && transformedData.tabs && transformedData.tabs.length > 0 && (
          <Box bg="white" borderRadius="xl" shadow="sm" mb={6} borderWidth={1} borderColor="gray.200">
            <Box borderBottomWidth={1} borderColor="primary.200">
              <HStack space={8} px={6} py={0}>
                {transformedData.tabs.map((tab: string) => (
                                      <Pressable
                      key={tab}
                      py={4}
                      px={1}
                      borderBottomWidth={2}
                      borderBottomColor={
                        activeTab === tab ? COLORS.TABS_ACTIVE_BORDER_COLOR : "transparent"
                      }
                      onPress={() => handleTabChange(tab)}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={
                          activeTab === tab ? COLORS.TABS_ACTIVE_BORDER_COLOR : COLORS.TEXT_COLOR
                        }
                      >
                        {tab}
                      </Text>
                    </Pressable>
                ))}
              </HStack>
            </Box>
          </Box>
        )}

        {/* Main Content - Different based on data type */}
        {mode === 'quotation-analysis' ? (
          <Box width="100%">
            {analysisData ? (
              <QuotationAnalysisDashboardV2 data={analysisData} eventDetails={eventDetails} />
            ) : (
              <NoDataFound dataName="quotation analysis data" />
            )}
          </Box>
        ) : multiVendorData ? (
          <Box width="100%">
            <MultiVendorBenchmarkingDashboard data={multiVendorData} />
          </Box>
        ) : transformedData ? (
          <EventAnalysisDashboard
            // tab1Data={transformedData.tab1Data}
            // tab2Data={transformedData.tab2Data}
            activeTab={activeTab}
            sessionData={sessionData}
            vendorTotalsFromSummary={transformedData.vendorTotalsFromSummary}
            transformedData={transformedData}
          />
        ) : benchmarkData ? (
          <BenchmarkJsonViewer data={benchmarkData} />
        ) : (
          <NoDataFound dataName="data" />
        )}
      </Box>

      {/* Loading Modal for Re-Run - Matching ABG Event Bench exactly */}
      {/* {!MobileWindow ? (
        <CustomModal
          // @ts-ignore - Component works correctly, matches documents/index.jsx pattern
          isOpen={showRerunLoadingModal}
          onClose={isRunningRerun ? () => {} : closeRerunLoadingModal}
          // @ts-ignore
          header={<Text>ABG Event Bench</Text>}
          // @ts-ignore
          body={
            <VStack space={4} alignItems="center" py={4}>
              {rerunError ? (
                <Text color="red.500" fontSize="sm" textAlign="center">
                  {rerunError}
                </Text>
              ) : (
                <HStack space={3} alignItems="center">
                  <Box
                    size={6}
                    borderWidth={3}
                    borderColor="orange.500"
                    borderTopColor="transparent"
                    borderRadius="full"
                    style={{
                      animation: 'spin 1s linear infinite'
                    } as any}
                  />
                  <Box
                    position="relative"
                    fontSize="md"
                    color="orange.500"
                    fontWeight="500"
                    style={{
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(239, 175, 130, 0.8) 50%, transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite linear',
                        pointerEvents: 'none'
                      } as any}
                    />
                    {shimmerTexts[shimmerTextIndex]}
                  </Box>
                </HStack>
              )}
            </VStack>
          }
          width={MobileWindow ? "100%" : "50%"}
        />
      ) : (
        <MobileModelSheet
          // @ts-ignore - Component works correctly, matches documents/index.jsx pattern
          isOpen={showRerunLoadingModal}
          onClose={isRunningRerun ? () => {} : closeRerunLoadingModal}
          // @ts-ignore
          header={<Text>ABG Event Bench</Text>}
          // @ts-ignore
          body={
            <VStack space={4} alignItems="center" py={4}>
              {rerunError ? (
                <Text color="red.500" fontSize="sm" textAlign="center">
                  {rerunError}
                </Text>
              ) : (
                <HStack space={3} alignItems="center">
                  <Box
                    size={6}
                    borderWidth={3}
                    borderColor="orange.500"
                    borderTopColor="transparent"
                    borderRadius="full"
                    style={{
                      animation: 'spin 1s linear infinite'
                    } as any}
                  />
                  <Box
                    position="relative"
                    fontSize="md"
                    color="orange.500"
                    fontWeight="500"
                    style={{
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(239, 175, 130, 0.8) 50%, transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite linear',
                        pointerEvents: 'none'
                      } as any}
                    />
                    {shimmerTexts[shimmerTextIndex]}
                  </Box>
                </HStack>
              )}
            </VStack>
          }
        />
      )} */}
    </Box>
  );
};

export default EventInsightsPage; 